import { type FunctionComponent } from 'react'
import { useState, useCallback } from 'react'
import { NotificationIcon } from '@instacart/ids-core'
import {
  NotificationsDrawer,
  useUnreadNotificationsWithToast,
} from '@retailer-platform/domain-notifications'
import { useCurrentAccountContext } from '../../../../utils/current-account/CurrentAccountContext'
import { navBarHeight } from './navBar.constants'

const NOTIFICATIONS_DRAWER_FEATURE = 'rt_notification_center'
const UNREAD_COUNT_POLLING_INTERVAL = 30000 // 30 seconds

const getTodayDateRange = () => {
  const today = new Date()
  const startDate = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 0, 0, 0, 0)
  )
  const endDate = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + 1, 0, 0, 0, 0)
  )
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  }
}

const getEarlierDateRange = () => {
  const today = new Date()
  const startDate = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - 1, 0, 0, 0, 0)
  )
  const endDate = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), 0, 0, 0, 0)
  )
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  }
}

type Props = {
  navBarColor: string
  navBarColorHover: string
}

export const NotificationsButton: FunctionComponent<Props> = ({
  navBarColor,
  navBarColorHover,
}) => {
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const currentAccountContext = useCurrentAccountContext()
  const isNotificationsEnabled = currentAccountContext?.account?.featureToggles?.find(
    experiment => experiment.name === NOTIFICATIONS_DRAWER_FEATURE
  )

  const {
    totalUnreadCount: unreadCount,
    refetch: refetchUnreadCount,
    onDrawerOpen,
  } = useUnreadNotificationsWithToast({
    pollingInterval: UNREAD_COUNT_POLLING_INTERVAL,
  })

  const onOpenNotifications = useCallback(() => {
    setNotificationsOpen(true)
    // Refetch and reset baseline when drawer opens
    onDrawerOpen()
  }, [onDrawerOpen])

  const onCloseNotifications = useCallback(() => {
    setNotificationsOpen(false)
    // Refetch unread count when drawer closes
    refetchUnreadCount()
  }, [refetchUnreadCount])

  if (!isNotificationsEnabled) {
    return null
  }

  return (
    <div css={{ position: 'relative' }}>
      <button
        css={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: navBarHeight,
          width: navBarHeight,
          backgroundColor: notificationsOpen ? 'white' : navBarColor,
          '&:hover': {
            backgroundColor: notificationsOpen ? 'white' : navBarColorHover,
          },
        }}
        onMouseDown={() => {
          if (notificationsOpen) {
            onCloseNotifications()
          } else {
            onOpenNotifications()
          }
        }}
      >
        <NotificationIcon
          size={24}
          css={{
            fill: notificationsOpen ? navBarColor : 'white',
          }}
        />
        {unreadCount > 0 && (
          <div
            css={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              backgroundColor: '#FF3B30',
              color: 'white',
              borderRadius: '8px',
              padding: '0 4px',
              fontSize: '12px',
              minWidth: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </div>
        )}
      </button>
      <NotificationsDrawer isOpen={notificationsOpen} onClose={onCloseNotifications} />
    </div>
  )
}
